import { useCallback, useEffect, useRef } from 'react';
import * as chatApi from '../../api/chat';

const HEARTBEAT_MS = 30_000;
const MAX_RETRIES = 12;
const MAX_BACKOFF_MS = 30_000;

// Read token at connect-time so a token rotation in another tab is picked
// up on the next reconnect (F-018). Falls back to the prop for compatibility.
function readTokenFromStorage(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

export function useChatWs(token: string | null, onEvent: (e: any) => void) {
  const wsRef = useRef<WebSocket | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  const hbTimer = useRef<ReturnType<typeof setInterval>>();
  const onEventRef = useRef(onEvent);
  const retryCount = useRef(0);
  const tokenRef = useRef<string | null>(token);
  const explicitlyClosed = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  });
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const connect = useCallback(() => {
    // F-018: re-read the token on every connect so a refresh from another
    // tab is honoured. Hook-prop wins when it's set; storage is the fallback.
    const liveToken = tokenRef.current || readTokenFromStorage();
    if (!liveToken) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    explicitlyClosed.current = false;
    // F-008: prefer header (Sec-WebSocket-Protocol) auth so the JWT does not
    // appear in proxy access logs. Browsers send the second WebSocket arg as
    // the offered subprotocol list.
    let ws: WebSocket;
    try {
      ws = new WebSocket(chatApi.getChatWsUrl(liveToken), [`bearer.${liveToken}`]);
    } catch {
      // Some browsers reject non-token-grammar subprotocols; fall back without.
      ws = new WebSocket(chatApi.getChatWsUrl(liveToken));
    }
    wsRef.current = ws;

    ws.onopen = () => {
      retryCount.current = 0;
      clearTimeout(retryTimer.current);
      hbTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat' }));
        }
      }, HEARTBEAT_MS);
    };

    ws.onmessage = (e) => {
      try {
        onEventRef.current(JSON.parse(e.data));
      } catch {
        /* malformed frame — ignore */
      }
    };

    ws.onclose = (ev) => {
      clearInterval(hbTimer.current);
      if (explicitlyClosed.current) return;

      // F-018: stop retrying after MAX_RETRIES so we don't spam dead servers
      // forever; surface a synthetic event so the UI can prompt the user.
      if (retryCount.current >= MAX_RETRIES) {
        try {
          onEventRef.current({ type: 'ws_disconnected', code: ev.code });
        } catch {
          /* noop */
        }
        return;
      }
      // 4001 = unauthenticated (server side). Don't tight-loop with a stale
      // token — let the auth layer refresh and bounce us via re-mount.
      if (ev.code === 4001) {
        try {
          onEventRef.current({ type: 'ws_unauthorized' });
        } catch {
          /* noop */
        }
        return;
      }

      const base = Math.min(1000 * 2 ** retryCount.current, MAX_BACKOFF_MS);
      // Full jitter — uniform in [base/2, base] avoids thundering-herd.
      const delay = Math.floor(base / 2 + Math.random() * (base / 2));
      retryCount.current += 1;
      retryTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, []);

  useEffect(() => {
    connect();

    // F-018: reconnect immediately when the browser comes back online; tear
    // down on offline so we don't waste retries while the network is dead.
    const onOnline = () => {
      retryCount.current = 0;
      clearTimeout(retryTimer.current);
      if (
        !wsRef.current ||
        wsRef.current.readyState === WebSocket.CLOSED ||
        wsRef.current.readyState === WebSocket.CLOSING
      ) {
        connect();
      }
    };
    const onOffline = () => {
      clearTimeout(retryTimer.current);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      explicitlyClosed.current = true;
      clearTimeout(retryTimer.current);
      clearInterval(hbTimer.current);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
  }, [connect]);

  return useCallback((data: object) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(data));
  }, []);
}
